import { useMemo, type ReactElement } from 'react';
import { AllowCameraArt } from '@/app/AllowCameraArt';
import {
  cameraBlockedSteps,
  detectCameraHelpPlatform,
  type CameraHelpPlatform,
} from '@/camera/cameraClient';
import { Button } from '@/ui/Button';
import { Screen } from '@/ui/Screen';

export const CAMERA_PRIMER_TITLE = 'Your phone will ask to use the camera.';
export const CAMERA_PRIMER_BODY = 'Tap Allow. Video never leaves your phone.';
export const CAMERA_GOT_IT_LABEL = 'Got it';
export const CAMERA_TRY_AGAIN_LABEL = 'Try again';
export const CAMERA_BLOCKED_TITLE = 'Camera is blocked';

export interface CameraPermissionCardProps {
  mode: 'primer' | 'blocked';
  platform?: CameraHelpPlatform;
  onGotIt?: () => void;
  onRetry?: () => void;
}

export function CameraPermissionCard({
  mode,
  platform,
  onGotIt,
  onRetry,
}: CameraPermissionCardProps): ReactElement {
  const client = useMemo(
    () => platform ?? detectCameraHelpPlatform(),
    [platform]
  );
  const steps = cameraBlockedSteps(client);

  return (
    <Screen className="bg-ma-bg">
      <div className="flex flex-1 flex-col justify-center py-6">
        <div className="rounded-ma border border-ma-line bg-ma-surface px-5 py-7 text-center shadow-[0_0_40px_rgba(77,182,165,0.16)]">
          {mode === 'primer' ? <AllowCameraArt /> : (
            <div className="mx-auto mb-2 flex h-20 w-20 items-center justify-center rounded-full bg-ma-accent/15 text-4xl" aria-hidden>
              📷
            </div>
          )}
          <h2 className="mt-2 text-[clamp(1.45rem,6.4vw,1.85rem)] font-bold leading-tight">
            {mode === 'primer' ? CAMERA_PRIMER_TITLE : CAMERA_BLOCKED_TITLE}
          </h2>
          <p className="mt-3 text-[clamp(1.05rem,4.4vw,1.2rem)] leading-snug text-ma-muted">
            {mode === 'primer' ? CAMERA_PRIMER_BODY : steps}
          </p>
          {mode === 'primer' ? (
            <Button block className="mt-7 text-xl" onClick={onGotIt}>
              {CAMERA_GOT_IT_LABEL}
            </Button>
          ) : (
            <Button block className="mt-7 text-xl" onClick={onRetry}>
              {CAMERA_TRY_AGAIN_LABEL}
            </Button>
          )}
        </div>
      </div>
    </Screen>
  );
}
